/**
 * User-tweakable settings, persisted to localStorage. Pure JSON shape, so
 * lossless round-trip. Anything missing is filled from DEFAULT_SETTINGS so
 * adding new fields is backwards-compatible.
 *
 * Note: highScore is only persisted on the game_over event in main.ts.
 * Losing the tab mid-run forfeits any in-progress score — same as a real
 * arcade reset.
 */

export type Settings = {
  readonly volume: number;
  readonly highScore: number;
  readonly musicEnabled: boolean;
};

export const DEFAULT_SETTINGS: Settings = {
  volume: 0.5,
  highScore: 0,
  musicEnabled: true,
};

const KEY = 'rally-xy.settings.v1';

export type Storage = {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
};

export function loadSettings(storage: Storage): Settings {
  try {
    const raw = storage.getItem(KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return {
      volume: typeof parsed.volume === 'number' ? clampVolume(parsed.volume) : DEFAULT_SETTINGS.volume,
      highScore:
        typeof parsed.highScore === 'number' && parsed.highScore >= 0 && Number.isFinite(parsed.highScore)
          ? Math.floor(parsed.highScore)
          : DEFAULT_SETTINGS.highScore,
      musicEnabled:
        typeof parsed.musicEnabled === 'boolean' ? parsed.musicEnabled : DEFAULT_SETTINGS.musicEnabled,
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function saveSettings(storage: Storage, settings: Settings): void {
  storage.setItem(KEY, JSON.stringify(settings));
}

function clampVolume(v: number): number {
  if (Number.isNaN(v)) return DEFAULT_SETTINGS.volume;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}
